import { useState, useEffect } from 'react'
import { signIn, signUp } from '../lib/db'
import { supabase, isSupabaseEnabled } from '../lib/supabase'
import { containsProfanity } from '../lib/profanityFilter'
import BrandMark from '../components/BrandMark'

// Read wb-region cookie set by middleware.js. Null = no cookie yet (fail-open).
function getRegionFromCookie() {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|;\s*)wb-region=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

const BLOCKED_REGIONS = new Set(['WA'])

export default function Auth({ onLogin, onClose }) {
  const [mode, setMode]   = useState('login')
  const [form, setForm]   = useState({ email:'', password:'', username:'' })
  const [error, setError] = useState('')
  const [info,  setInfo]  = useState('')  // success / informational alerts (password reset)
  const [loading, setLoading] = useState(false)

  // Initial region is null (cookie not yet read). Gate fails open during this
  // fraction-of-a-second window — acceptable for soft launch; tightening would
  // require SSR which the SPA doesn't have.
  const [region, setRegion] = useState(null)
  useEffect(() => { setRegion(getRegionFromCookie()) }, [])

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError(''); setInfo('')
    if (!form.email || !form.password) { setError('Please fill in all fields'); return }
    if (mode === 'signup' && !form.username) { setError('Username is required'); return }
    if (mode === 'signup' && containsProfanity(form.username)) {
      setError('Please choose a different username.')
      return
    }
    setLoading(true)
    try {
      const user = mode === 'login'
        ? await signIn(form.email, form.password)
        : await signUp(form.email, form.password, form.username)
      onLogin(user)
    } catch (err) {
      setError(err.message || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  // Sends Supabase's built-in reset-password email. Redirect lands the user
  // back on the deployed app (window.location.origin works for both prod
  // and the local Vite dev server without hardcoding URLs).
  async function handleForgotPassword() {
    setError(''); setInfo('')
    if (!form.email) { setError('Enter your email above first'); return }
    if (!isSupabaseEnabled) {
      setError('Password reset requires Supabase to be configured')
      return
    }
    setLoading(true)
    try {
      const redirectTo = typeof window !== 'undefined' ? window.location.origin : undefined
      const { error: err } = await supabase.auth.resetPasswordForEmail(form.email, { redirectTo })
      if (err) setError(err.message)
      else setInfo('Password reset email sent — check your inbox.')
    } catch (err) {
      setError(err?.message || 'Could not send reset email')
    } finally {
      setLoading(false)
    }
  }

  // Overlay positioning: position:'fixed' + inset:0 + zIndex:200 makes
  // Auth a true overlay covering the viewport. It WAS normal-flow (just
  // minHeight:100vh) — mounted inline via {showAuthPrompt && ...} it
  // appended after page content instead of covering it. overflowY:'auto'
  // lets the form scroll within the overlay when the on-screen keyboard
  // shrinks viewport height on mobile. zIndex 200 sits above bottom-nav
  // (50), modal-backdrop (100), and notification (120). Same style is
  // applied to BOTH the BLOCKED_REGIONS branch and the main return.
  if (region && BLOCKED_REGIONS.has(region)) {
    return (
      <div style={{ position:'fixed', inset:0, zIndex:200, overflowY:'auto', background:'#08080e', display:'flex', flexDirection:'column', justifyContent:'center', padding:'24px 20px' }}>
        {/* Same dismiss affordance as the main return — without it, a WA
            user who opened the overlay via requireAuth would be stuck on
            the blocked message. position:'fixed' on the parent creates
            the containing block that anchors the X. */}
        {onClose && (
          <button onClick={onClose} aria-label="Close"
            style={{ position:'absolute', top:16, right:16, width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.05)', border:'1px solid var(--border)', color:'var(--text-dim)', fontSize:'20px', lineHeight:1, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
            ×
          </button>
        )}
        <div style={{ textAlign:'center', marginBottom:'36px' }}>
          <BrandMark variant="lockup" height={260} />
        </div>
        <div style={{ maxWidth:'420px', margin:'0 auto', textAlign:'center', color:'var(--text)', lineHeight:'1.6' }}>
          <h2 style={{ fontSize:'22px', marginBottom:'16px', color:'var(--text)' }}>
            Pool Party Sports isn't available in Washington yet
          </h2>
          <p style={{ color:'#888', fontSize:'14px', marginBottom:'20px' }}>
            We're working on bringing the platform to more states.
          </p>
          <p style={{ color:'#666', fontSize:'13px' }}>
            If you're not in Washington and you're seeing this message in error, contact{' '}
            <a href="mailto:cadenhamlett12@gmail.com" style={{ color:'var(--gold)' }}>cadenhamlett12@gmail.com</a>{' '}
            and we'll help.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, overflowY:'auto', background:'#08080e', display:'flex', flexDirection:'column', justifyContent:'center', padding:'24px 20px' }}>
      {/* Close affordance. Auth is now always mounted as a dismissable
          overlay (the front-door wall was removed in cdcfba1), so
          onClose is always passed and the X always renders — the
          onClose guard is just belt-and-suspenders for any future
          caller that omits it. Anchored to the container via the
          parent's position:'fixed'. */}
      {onClose && (
        <button onClick={onClose} aria-label="Close"
          style={{ position:'absolute', top:16, right:16, width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.05)', border:'1px solid var(--border)', color:'var(--text-dim)', fontSize:'20px', lineHeight:1, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
          ×
        </button>
      )}
      {/* Logo */}
      <div style={{ textAlign:'center', marginBottom:'36px' }}>
        <BrandMark variant="lockup" style={{ width:'100%', maxWidth:'320px', height:'auto' }} />
        <div style={{ color:'#666', fontSize:'14px', marginTop:'12px' }}>Pick'em and bracket pools with your friends.</div>
      </div>

      {/* Toggle */}
      <div className="tab-bar" style={{ marginBottom:'20px' }}>
        <button className={`tab-item ${mode==='login'?'active':''}`} onClick={() => { setMode('login'); setError('') }}>Sign In</button>
        <button className={`tab-item ${mode==='signup'?'active':''}`} onClick={() => { setMode('signup'); setError('') }}>Create Account</button>
      </div>

      <form onSubmit={handleSubmit}>
        {mode === 'signup' && (
          <div className="form-group">
            <label className="form-label">Username</label>
            <input placeholder="YourUsername" value={form.username} onChange={set('username')} autoComplete="username" />
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Email</label>
          <input type="email" placeholder="you@email.com" value={form.email} onChange={set('email')} autoComplete="email" />
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input type="password" placeholder="••••••••" value={form.password} onChange={set('password')} autoComplete={mode==='login'?'current-password':'new-password'} />
          {mode === 'login' && (
            <div style={{ textAlign:'right', marginTop:'6px' }}>
              <button type="button" onClick={handleForgotPassword} disabled={loading}
                style={{ background:'none', border:'none', color:'var(--text-dim)', fontSize:'13px', padding:0, cursor: loading ? 'default' : 'pointer', textDecoration:'underline', fontFamily:'inherit' }}>
                Forgot password?
              </button>
            </div>
          )}
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {info  && <div className="alert alert-success">{info}</div>}

        <button className="btn btn-gold" type="submit" disabled={loading} style={{ marginBottom:'12px' }}>
          {loading ? 'Please wait…' : mode === 'login' ? 'Sign In' : 'Create Account'}
        </button>
      </form>

      <p style={{ textAlign:'center', color:'#444', fontSize:'12px', marginTop:'28px', lineHeight:'1.6' }}>
        By continuing you agree to our <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>Terms of Service</a> and <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)' }}>Privacy Policy</a>.<br />
        Must be 13 or older to use Pool Party Sports.
      </p>
    </div>
  )
}
